import { useState } from 'react';

interface SubmitEventModalProps {
  onClose: () => void;
}

export default function SubmitEventModal({ onClose }: SubmitEventModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));

    try {
      await fetch('https://readdy.ai/api/form/d6t0f0id1d31f3q967l0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Submit a Local Event
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">We review all submissions before listing</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full cursor-pointer hover:bg-gray-200 transition-colors"
          >
            <i className="ri-close-line text-gray-600"></i>
          </button>
        </div>

        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-pink-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-pink-500 text-3xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Got it!</h3>
            <p className="text-sm text-gray-500 mb-6">
              We&apos;ll review and add it if it&apos;s a good fit.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-pink-500 hover:bg-pink-600 text-white text-sm font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        ) : (
          <form
            data-readdy-form
            id="local-event-submission"
            onSubmit={handleSubmit}
            className="p-6 space-y-4"
          >
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Event Name <span className="text-pink-500">*</span></label>
              <input
                type="text"
                name="event_name"
                required
                placeholder="e.g. Summer Splash Playdate"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Date <span className="text-pink-500">*</span></label>
                <input
                  type="date"
                  name="event_date"
                  required
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Time</label>
                <input
                  type="time"
                  name="event_time"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Location / Venue <span className="text-pink-500">*</span></label>
              <input
                type="text"
                name="location"
                required
                placeholder="e.g. Vines Park, Loganville GA"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Brief Description <span className="text-pink-500">*</span>
                <span className="text-gray-400 font-normal ml-1">({charCount}/500)</span>
              </label>
              <textarea
                name="description"
                required
                maxLength={500}
                rows={3}
                placeholder="What's this event about? Who is it for?"
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Event Link <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="url"
                name="event_link"
                placeholder="https://..."
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Your Name <span className="text-pink-500">*</span></label>
              <input
                type="text"
                name="submitter_name"
                required
                placeholder="First and last name"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Your Email <span className="text-pink-500">*</span></label>
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
              />
            </div>

            <div className="pt-1">
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70"
              >
                {submitting ? 'Submitting...' : 'Submit for Review'}
              </button>
              <p className="text-center text-xs text-gray-400 mt-2">All submissions are reviewed before being listed.</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
